// src/pages/Documents/AddDocuments.js
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import ListDocuments from "./ListDocuments";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Divider,
  Button,
  CircularProgress,
  useTheme,
  Modal,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CreateIcon from "@mui/icons-material/Create";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { hasPermission } from "../../../utils/permissionUtils";
import { Document, Packer, Paragraph } from "docx";

// Tiptap Imports
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";

// Import TiptapEditor component
import TiptapEditor from "../../../components/Common/TiptapEditor";

// Ant Design Imports
import { Select } from "antd";

// Document Upload Context
import { useDocumentUpload } from "../../../contexts/DocumentUploadContext";

const { Option } = Select;

const ensureUrlProtocol = (url) => {
  if (!url) return "";
  if (!/^(https?:\/\/|mailto:|tel:)/i.test(url)) {
    return "https://" + url;
  }
  return url;
};

export default function AddDocuments() {
  const { chatbotId: paramsChatbotId } = useParams();
  const theme = useTheme();
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // State for categories
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [websiteLink, setWebsiteLink] = useState("");

  // Upload limit state
  const [uploadLimit, setUploadLimit] = useState(null);

  // Modal states for "Create New Document"
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newFilename, setNewFilename] = useState("");
  const [modalWebsiteLink, setModalWebsiteLink] = useState("");
  const [modalSelectedCategories, setModalSelectedCategories] = useState([]);
  const [createError, setCreateError] = useState("");
  const [createLoading, setCreateLoading] = useState(false);

  // Use global upload context
  const {
    isUploading,
    startFileUpload,
    startDocumentUpload,
    registerOnComplete,
    unregisterOnComplete,
  } = useDocumentUpload();

  // Register callback to refresh document list when upload completes
  useEffect(() => {
    registerOnComplete(() => {
      setRefresh((prev) => !prev);
      fetchUploadLimit();
    });
    return () => unregisterOnComplete();
  }, [registerOnComplete, unregisterOnComplete]);

  const fetchCategories = useCallback(async () => {
    if (!paramsChatbotId) return;
    try {
      const params = { chatbot_id: paramsChatbotId };
      const response = await axiosInstance.get('/api/categories', { params });
      setCategories(response.data.data);
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  }, [paramsChatbotId]);

  const fetchUploadLimit = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/get-document-upload-limit');
      setUploadLimit(response.data);
    } catch (error) {
      console.error("Failed to fetch upload limit", error);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchUploadLimit();
  }, [fetchCategories, fetchUploadLimit, refresh]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      Underline,
    ],
    content: "",
    editorProps: {
      attributes: { class: "tiptap-editor-area" },
    },
  });

  useEffect(() => {
    document.title = "Upload Document | KlonIT Web Application";
  }, []);

  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission("documents", "view");
        const modifyPerm = await hasPermission("documents", "add|edit");
        const deletePerm = await hasPermission("documents", "delete");

        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      setSelectedFiles(null);
      return;
    }

    // Check subscription limit before accepting files
    if (uploadLimit) {
      if (files.length > uploadLimit.remaining_slots) {
        setError(
          `You can only upload ${uploadLimit.remaining_slots} more document(s). ` +
          `You currently have ${uploadLimit.current_count} out of ${uploadLimit.max_limit} allowed. ` +
          `You selected ${files.length} file(s). Please reduce your selection or upgrade your plan.`
        );
        event.target.value = "";
        setSelectedFiles(null);
        return;
      }
    }

    setError("");
    setSelectedFiles(files);
  };

  const handleFileUpload = async () => {
    if (!selectedFiles) {
      setError("Please select file(s) to upload.");
      return;
    }

    // Re-check limit before upload
    if (uploadLimit && selectedFiles.length > uploadLimit.remaining_slots) {
      setError(
        `You can only upload ${uploadLimit.remaining_slots} more document(s). ` +
        `You selected ${selectedFiles.length} file(s). Please reduce your selection or upgrade your plan.`
      );
      return;
    }

    setError("");

    // Convert FileList to Array BEFORE clearing the input,
    // because FileList is a live DOM reference that gets emptied when input is cleared
    const filesArray = Array.from(selectedFiles);

    // Reset local form state first, then start upload with the safe copy
    setSelectedFiles(null);
    setSelectedCategories([]);
    setWebsiteLink("");
    const fileInput = document.getElementById("file-upload-input");
    if (fileInput) fileInput.value = "";

    // Delegate to global context with the safe array copy
    startFileUpload(filesArray, paramsChatbotId, selectedCategories, websiteLink);
  };

  const handleCategoriesUpdated = () => {
    fetchCategories();
  };

  // --- Modal Logic ---
  const handleOpenModal = () => {
    setIsModalOpen(true);
    setNewFilename("");
    setModalWebsiteLink("");
    setModalSelectedCategories([]);
    setCreateError("");
    if (editor) editor.commands.clearContent();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const convertHtmlToDocx = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const paragraphs = [];
    // ... function implementation
    return paragraphs;
  };

  const handleCreateDocument = async () => {
    setCreateError("");
    if (!newFilename.trim()) {
      setCreateError("Please enter a filename.");
      return;
    }
    if (!editor || editor.isEmpty) {
      setCreateError("Please enter some content in the editor.");
      return;
    }
    if (modalWebsiteLink.trim()) {
      try {
        new URL(ensureUrlProtocol(modalWebsiteLink.trim()));
      } catch {
        setCreateError("Please enter a valid website link.");
        return;
      }
    }

    // Check subscription limit for single document creation
    if (uploadLimit && uploadLimit.remaining_slots <= 0) {
      setCreateError(
        `You have reached the maximum document limit (${uploadLimit.max_limit}). Please upgrade your plan.`
      );
      return;
    }

    setCreateLoading(true);

    try {
      const htmlContent = editor.getHTML();
      const doc = new Document({ sections: [ { properties: {}, children: [new Paragraph(htmlContent)] } ]});
      const blob = await Packer.toBlob(doc);

      const finalFilename = newFilename.trim().endsWith(".docx")
        ? newFilename.trim()
        : `${newFilename.trim()}.docx`;
      const file = new File([blob], finalFilename, {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      // Delegate to global context
      startDocumentUpload(file, paramsChatbotId, modalSelectedCategories, modalWebsiteLink);

      handleCloseModal();
    } catch (error) {
      console.error("Error creating document:", error);
      if (error.response) {
        setCreateError(error.response.data.message);
      } else {
        setCreateError("Failed to create document. Please try again.");
      }
    } finally {
      setCreateLoading(false);
    }
  };

  if (checkingPermissions) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={30} />
        <Typography mt={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px" }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You do not have permission to view documents.</Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px" }}>
        <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
          Manage <strong>Documents</strong>
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              margin: '0 auto',
              maxWidth: 600,
            }}
          >
            <Typography variant="h6" color="textPrimary">
              Upload Documents
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
              Upload document files (.pdf, .doc, .docx, .txt, etc.)
            </Typography>

            {/* Upload in progress warning */}
            {isUploading && (
              <Alert severity="warning" sx={{ width: "100%" }}>
                A document upload is currently in progress. Please wait for it to finish before uploading new files.
              </Alert>
            )}

            {/* Document limit info */}
            {uploadLimit && (
              <Alert
                severity={uploadLimit.remaining_slots <= 0 ? "warning" : "info"}
                sx={{ width: "100%" }}
                icon={<UploadFileIcon />}
              >
                Documents: {uploadLimit.current_count} / {uploadLimit.max_limit} used
                {uploadLimit.remaining_slots > 0
                  ? ` (${uploadLimit.remaining_slots} remaining)`
                  : " — Limit reached. Please upgrade your plan."}
              </Alert>
            )}

            {/* 1. Choose Files Button */}
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" justifyContent="center">
              <Button
                variant="outlined"
                component="label"
                disabled={isUploading || (uploadLimit && uploadLimit.remaining_slots <= 0)}
                sx={{ minWidth: 200 }}
              >
                Choose Files
                <input
                  id="file-upload-input"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.md"
                  onChange={handleFileChange}
                  hidden
                />
              </Button>
              {selectedFiles && (
                <Typography variant="body2" color="textSecondary">
                  {selectedFiles.length} file(s) selected
                </Typography>
              )}
            </Box>

            {/* 2. Select Categories (Moved) */}
            <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Select Categories (Optional)
                </Typography>
                <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Defaults to 'Uncategorized' if none selected"
                    onChange={setSelectedCategories}
                    value={selectedCategories}
                    disabled={isUploading}
                >
                    {categories.map(cat => (
                        <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                    ))}
                </Select>
            </Box>

            {/* 3. Source Website Link (Added) */}
            <TextField
              fullWidth
              label="Optional: Source Website Link"
              value={websiteLink}
              onChange={(e) => setWebsiteLink(e.target.value)}
              variant="outlined"
              size="small"
              disabled={isUploading}
              placeholder="https://example.com/source-of-documents"
            />

            {/* Upload Button */}
            {canModify && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleFileUpload}
                  disabled={isUploading || !selectedFiles || (uploadLimit && uploadLimit.remaining_slots <= 0)}
                  startIcon={ isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon /> }
                  sx={{ width: '100%', maxWidth: '350px' }}
                >
                  {isUploading ? "Upload in Progress..." : "Upload Files"}
                </Button>
            )}

            {canModify && (
              <>
                <Typography variant="body2" color="textSecondary" sx={{ my: 2, textAlign: "center" }}>
                  ─────── OR ───────
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleOpenModal}
                  startIcon={<CreateIcon />}
                  disabled={isUploading || (uploadLimit && uploadLimit.remaining_slots <= 0)}
                >
                  Create New Document (Text Editor)
                </Button>
              </>
            )}

            {error && (
              <Alert
                severity={ error.includes("success") || error.includes("successfully") ? "success" : "error" }
                sx={{ mt: 2, width: '100%' }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box>
          <Typography variant="h6" sx={{ mb: 2 }} color="textPrimary">
            Uploaded Documents
          </Typography>
          <ListDocuments
            refresh={refresh}
            chatbotId={paramsChatbotId}
            permission={{ canView, canModify, canDelete }}
            categories={categories}
            onCategoriesUpdate={handleCategoriesUpdated}
          />
        </Box>
      </Paper>

      {/* Create Document Modal */}
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: "900px" },
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Create New Document (.docx)
            </Typography>
            <IconButton onClick={handleCloseModal} disabled={createLoading}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />

          <Box sx={{ flexGrow: 1, overflowY: "auto", p: 3 }}>
            {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}

            <TextField
              fullWidth
              label="Filename"
              value={newFilename}
              onChange={(e) => setNewFilename(e.target.value)}
              margin="normal"
              required
              disabled={createLoading}
              helperText=".docx will be added automatically"
              error={createError.toLowerCase().includes("filename")}
            />

            {/* Select Categories in Modal (Added) */}
            <Box sx={{ my: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select Categories (Optional)
              </Typography>
              <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Defaults to 'Uncategorized' if none selected"
                  onChange={setModalSelectedCategories}
                  value={modalSelectedCategories}
              >
                  {categories.map(cat => (
                      <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                  ))}
              </Select>
            </Box>

            <TextField
              fullWidth
              label="Optional: Source Website Link"
              value={modalWebsiteLink}
              onChange={(e) => setModalWebsiteLink(e.target.value)}
              margin="normal"
              disabled={createLoading}
              placeholder="https://example.com/related-to-this-doc"
              error={createError.toLowerCase().includes("link")}
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Document Content *
              </Typography>
              {/* Use TiptapEditor component */}
              <TiptapEditor editor={editor} height="300px" />
            </Box>
          </Box>
          <Divider />
          <Stack direction="row" spacing={2} sx={{ p: 2 }}>
            <Button variant="outlined" color="secondary" onClick={handleCloseModal} disabled={createLoading} fullWidth>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateDocument}
              disabled={createLoading || !newFilename.trim() || editor?.isEmpty}
              startIcon={createLoading ? <CircularProgress size={20} /> : <SaveIcon />}
              fullWidth
            >
              {createLoading ? "Saving..." : "Save Document"}
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Global styles remain the same */}
      <style jsx global>{`
        .ant-select-dropdown,
        .ant-dropdown-menu {
          z-index: 1350 !important;
        }
      `}</style>
    </>
  );
}
